import { useQuery } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { overtimeSatisticApi, userApi } from '../../../../apis';
import {
  EmployeeRelatedInformationDtoRolesEnum,
  StatisticsOvertimeOverviewJudgmentEnum,
} from '../../../../apis/client-axios';
import TableWrap from '../../../../components/TableWrap';
import Widget from '../../../../components/Widget';
import { OVERTIME_SATISTIC, QUERY_KEY } from '../../../../constants/QueryKey';
import { ADMIN_ROUTE_NAME } from '../../../../constants/RouteName';
import { RootState } from '../../../../store';

export const AnnualOvertimeDetail = () => {
  const intl = useIntl();
  const year = dayjs().year();
  const location = useLocation();
  const { employeeId } = useParams();
  const managerId = useSelector((state: RootState) => state.auth.authUser?.id);
  const isManagerRoute = location.pathname.includes(ADMIN_ROUTE_NAME.ANNUAL_CONFIRMATION);
  const id = isManagerRoute ? managerId : employeeId;
  const regulationTableRef = useRef<HTMLDivElement | null>(null);
  const [rowWidth, setRowWidth] = useState<number>(0);
  const role = useSelector((state: RootState) => state.auth.authUser?.role);
  const navigate = useNavigate();
  const calculateRowWidth = () => {
    if (regulationTableRef.current) {
      const tableWidth = regulationTableRef.current.offsetWidth;
      const columnWidth = tableWidth * 0.629560915;
      setRowWidth(tableWidth - columnWidth);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      calculateRowWidth();
    };

    const observer = new ResizeObserver(() => {
      calculateRowWidth();
    });

    if (regulationTableRef.current) {
      observer.observe(regulationTableRef.current);
    }

    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  useEffect(() => {
    if (role && role !== EmployeeRelatedInformationDtoRolesEnum.Manager) {
      navigate(-1);
    }
  }, [navigate, role]);

  const { data: overTimeData, isLoading } = useQuery({
    queryKey: [OVERTIME_SATISTIC],
    queryFn: () => overtimeSatisticApi.overtimeControllerStatisticsOvertime(year as number, id as string),
  });
  const { data: userData } = useQuery({
    queryKey: [QUERY_KEY.USER_BY_ID, employeeId],
    queryFn: () => userApi.userControllerGetUserById(employeeId as string),
    enabled: !!employeeId,
  });
  const externalData = [
    {
      regulation: intl.formatMessage(
        { id: 'annual.overtime.detail.column.yearlyOvertimeLimit' },
        { yearlyOvertimeLimit: overTimeData?.data.data?.overtimeSettings.yearlyOvertimeLimit },
      ),
    },
    {
      regulation: intl.formatMessage(
        { id: 'annual.overtime.detail.column.avgMonthlyOvertime' },
        { avgMonthlyOvertime: overTimeData?.data.data?.overtimeSettings.avgMonthlyOvertime },
      ),
    },
    {
      regulation: intl.formatMessage(
        { id: 'annual.overtime.detail.column.singleMonthLimit' },
        { singleMonthLimit: overTimeData?.data.data?.overtimeSettings.singleMonthLimit },
      ),
    },
    {
      regulation: intl.formatMessage(
        { id: 'annual.overtime.detail.column.overtimeExceed' },
        { overtimeExceed: overTimeData?.data.data?.overtimeSettings.overtimeExceed },
      ),
    },
  ];

  const dataSource = overTimeData?.data.data?.overview?.map((item, index) => ({
    ...item,
    regulation: externalData[index]?.regulation,
  }));

  const regulation = [
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.regulation' }),
      dataIndex: 'regulation',
      key: 'regulation',
      render: (value, record) => {
        return <p className="annual-overtime_detail-table-column-data">{record?.regulation}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.judgement' }),
      dataIndex: 'judgment',
      key: 'judgment',
      render: (value, record) => {
        return (
          <p className={`annual-overtime_detail-table-column-data`}>
            {intl.formatMessage({ id: `annual.overtime.detail.column.judgment.${record?.judgment}` })}
          </p>
        );
      },
    },

    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.asToday' }),
      dataIndex: 'asOfToday',
      key: 'asOfToday',
      render: (value, record) => {
        return <p className="annual-overtime_detail-table-column-data">{record?.asOfToday}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.upper' }),
      dataIndex: 'upperLimit',
      key: 'upperLimit',
      render: (value, record) => {
        return <p className="annual-overtime_detail-table-column-data">{record?.upperLimit}</p>;
      },
    },
  ];
  const columns = [
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.year' }, { year: year }),
      dataIndex: 'month',
      key: 'month',
      render: (value, record, index) => {
        const columnMonth = record?.month;
        return (
          <p className="annual-overtime_detail-table-column-data">
            {columnMonth === 'all'
              ? intl.formatMessage({ id: 'annual.overtime.detail.column.all' })
              : intl.formatMessage({ id: 'annual.overtime.detail.column.month' }, { month: columnMonth })}
          </p>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.weekdays' }),
      dataIndex: 'weekday',
      key: 'weekday',
      render: (record) => {
        return <p className="annual-overtime_detail-table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.scheduled' }),
      dataIndex: 'designatedHoliday',
      key: 'designatedHoliday',
      render: (record) => {
        return <p className="annual-overtime_detail-table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.both' }),
      dataIndex: 'weekdayPlusDesignated',
      key: 'weekdayPlusDesignated',
      render: (record) => {
        return <p className="annual-overtime_detail-table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.statutory' }),
      dataIndex: 'legalHoliday',
      key: 'legalHoliday',
      render: (record) => {
        return <p className="annual-overtime_detail-table-column-data">{record}</p>;
      },
    },
    {
      title: intl.formatMessage({ id: 'annual.overtime.detail.column.total' }),
      dataIndex: 'total',
      key: 'total',
      render: (record) => {
        return <p className="annual-overtime_detail-table-column-data">{record}</p>;
      },
    },
  ];
  return (
    <div className="annual-overtime_detail">
      <Widget
        breadcrumbs={[
          !employeeId
            ? intl.formatMessage({ id: 'annual.overtime.manager.breadcumb' })
            : intl.formatMessage({ id: 'annual.overtime.employee.breadcumb' }),
        ]}
        content={
          <div className="annual-overtime_detail-content">
            <TableWrap
              isPagination={false}
              loading={isLoading}
              tableProps={{
                dataSource: dataSource,
                columns: regulation,
                className: 'annual-overtime_detail-content-regulation',
                rowClassName: (record) => {
                  return record.judgment === StatisticsOvertimeOverviewJudgmentEnum.Ok
                    ? 'judgement-ok'
                    : record.judgment === StatisticsOvertimeOverviewJudgmentEnum.Ng
                      ? 'judgement-ng'
                      : record.judgment === StatisticsOvertimeOverviewJudgmentEnum.Notice
                        ? 'judgement-notice'
                        : '';
                },
              }}
              tableWrapperRef={regulationTableRef}
              rightTopContent={
                <Row
                  align="middle"
                  justify={'center'}
                  className="annual-overtime_detail-content-rightTopContent"
                  style={{ width: `${rowWidth}px` }}
                >
                  <Col>{overTimeData?.data?.data?.startYear}</Col>
                  <Col>~</Col>
                  <Col>{overTimeData?.data?.data?.endYear}</Col>
                </Row>
              }
            />
            <TableWrap
              isPagination={false}
              loading={isLoading}
              tableProps={{
                dataSource: overTimeData?.data.data?.detail || [],
                columns,
                className: 'annual-overtime_detail-content-table',
              }}
            />
          </div>
        }
        title={
          !employeeId
            ? intl.formatMessage({ id: 'annual.overtime.manager.breadcumb' })
            : userData?.data?.data?.name || ''
        }
      />
    </div>
  );
};
